/**
 * Build Phase Timeline - Visual Progress Tracker for Multi-Phase Builds
 *
 * Shows progression through: Validation → Testing → Building → Deployment → Complete
 * Provides time estimates, current status, and phase-specific details.
 */

import React from 'react';
import {
  CheckCircle,
  Circle,
  Clock,
  AlertTriangle,
  Activity,
  Shield,
  TestTube,
  Hammer,
  Upload,
  Flag
} from 'lucide-react';

export type PhaseStatus = 'pending' | 'in_progress' | 'completed' | 'failed' | 'skipped';

export interface PhaseInfo {
  id: string;
  name: string;
  description: string;
  status: PhaseStatus;
  startTime?: string;
  endTime?: string;
  duration?: number;
  estimatedDuration?: number;
  icon: React.ReactNode;
  details?: string[];
  warnings?: string[];
  errors?: string[];
}

interface BuildPhaseTimelineProps {
  phases: PhaseInfo[];
  currentPhaseId?: string;
  compact?: boolean;
  inline?: boolean;
}

export const BuildPhaseTimeline: React.FC<BuildPhaseTimelineProps> = ({
  phases,
  currentPhaseId,
  compact = false,
  inline = false
}) => {
  const getStatusIcon = (status: PhaseStatus, icon: React.ReactNode) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="text-green-400" size={compact ? 16 : 20} />;
      case 'in_progress':
        return <div className="text-blue-400">{icon}</div>;
      case 'failed':
        return <AlertTriangle className="text-red-400" size={compact ? 16 : 20} />;
      case 'skipped':
        return <Circle className="text-gray-600" size={compact ? 16 : 20} />;
      case 'pending':
      default:
        return <Circle className="text-gray-500" size={compact ? 16 : 20} />;
    }
  };

  const getStatusColor = (status: PhaseStatus) => {
    switch (status) {
      case 'completed':
        return 'border-green-500 bg-green-500/10';
      case 'in_progress':
        return 'border-blue-500 bg-blue-500/10 animate-pulse';
      case 'failed':
        return 'border-red-500 bg-red-500/10';
      case 'skipped':
        return 'border-gray-600 bg-gray-600/10';
      case 'pending':
      default:
        return 'border-gray-700 bg-gray-800/30';
    }
  };

  const formatDuration = (duration?: number) => {
    if (!duration) return null;
    if (duration < 60) return `${Math.round(duration)}s`;
    const mins = Math.floor(duration / 60);
    const secs = Math.round(duration % 60);
    return `${mins}m ${secs}s`;
  };

  if (compact) {
    // Compact horizontal timeline
    const timelineContent = (
      <div className="flex items-center justify-between">
        {phases.map((phase, idx) => {
          const isActive = phase.id === currentPhaseId;
          const isLast = idx === phases.length - 1;

          return (
            <React.Fragment key={phase.id}>
              <div className={`flex items-center gap-2 ${isActive ? 'scale-110' : ''} transition-transform`}>
                {getStatusIcon(phase.status, phase.icon)}
                <div className="hidden sm:block">
                  <p className={`text-xs font-medium ${
                    phase.status === 'completed' ? 'text-green-300' :
                    phase.status === 'in_progress' ? 'text-blue-300' :
                    phase.status === 'failed' ? 'text-red-300' :
                    'text-gray-500'
                  }`}>
                    {phase.name}
                  </p>
                  {phase.duration && (
                    <p className="text-xs text-gray-500">{formatDuration(phase.duration)}</p>
                  )}
                </div>
              </div>
              {!isLast && (
                <div className={`flex-1 h-0.5 mx-2 ${
                  phase.status === 'completed' ? 'bg-green-500' : 'bg-gray-700'
                }`} />
              )}
            </React.Fragment>
          );
        })}
      </div>
    );

    if (inline) {
      return timelineContent;
    }

    return (
      <div className="bg-gray-900/30 border border-gray-700 rounded-lg p-3">
        {timelineContent}
      </div>
    );
  }

  // Full detailed timeline
  return (
    <div className="bg-gray-900/30 border border-gray-700 rounded-lg">
      <div className="p-3 border-b border-gray-700">
        <h3 className="text-sm font-semibold text-white flex items-center gap-2">
          <Activity className="text-blue-400" size={16} />
          Build Pipeline Progress
        </h3>
      </div>
      <div className="p-4 space-y-3">
        {phases.map((phase, idx) => {
          const isActive = phase.id === currentPhaseId;
          const isLast = idx === phases.length - 1;

          return (
            <div key={phase.id} className="relative">
              {/* Timeline Connector */}
              {!isLast && (
                <div className={`absolute left-5 top-12 bottom-0 w-0.5 ${
                  phase.status === 'completed' ? 'bg-green-500/50' : 'bg-gray-700'
                }`} />
              )}

              {/* Phase Card */}
              <div className={`border-l-4 rounded-lg ${getStatusColor(phase.status)}`}>
                <div className="p-3">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-start gap-3 flex-1">
                      <div className={isActive ? 'animate-pulse' : ''}>
                        {getStatusIcon(phase.status, phase.icon)}
                      </div>
                      <div className="flex-1">
                        <h4 className="text-sm font-semibold text-white flex items-center gap-2">
                          {phase.name}
                          {phase.status === 'in_progress' && (
                            <span className="text-xs text-blue-400 animate-pulse">●</span>
                          )}
                        </h4>
                        <p className="text-xs text-gray-400 mt-0.5">{phase.description}</p>
                      </div>
                    </div>

                    {/* Timing Info */}
                    <div className="text-right ml-3">
                      {phase.status === 'in_progress' && phase.estimatedDuration && (
                        <div className="flex items-center gap-1 text-xs text-blue-300">
                          <Clock size={12} />
                          <span>~{formatDuration(phase.estimatedDuration)}</span>
                        </div>
                      )}
                      {phase.duration && (
                        <p className="text-xs text-gray-500">{formatDuration(phase.duration)}</p>
                      )}
                      {phase.startTime && (
                        <p className="text-xs text-gray-600">
                          {new Date(phase.startTime).toLocaleTimeString()}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Details */}
                  {phase.details && phase.details.length > 0 && (
                    <div className="ml-9 space-y-1">
                      {phase.details.map((detail, didx) => (
                        <p key={didx} className="text-xs text-gray-400 flex items-start gap-2">
                          <span className="text-blue-400 mt-0.5">•</span>
                          <span>{detail}</span>
                        </p>
                      ))}
                    </div>
                  )}

                  {/* Warnings */}
                  {phase.warnings && phase.warnings.length > 0 && (
                    <div className="ml-9 mt-2 bg-yellow-900/20 border border-yellow-600/30 rounded p-2">
                      {phase.warnings.map((warning, widx) => (
                        <p key={widx} className="text-xs text-yellow-300 flex items-start gap-2">
                          <AlertTriangle size={12} className="mt-0.5 flex-shrink-0" />
                          <span>{warning}</span>
                        </p>
                      ))}
                    </div>
                  )}

                  {/* Errors */}
                  {phase.errors && phase.errors.length > 0 && (
                    <div className="ml-9 mt-2 bg-red-900/20 border border-red-600/30 rounded p-2">
                      {phase.errors.map((error, eidx) => (
                        <p key={eidx} className="text-xs text-red-300 flex items-start gap-2">
                          <AlertTriangle size={12} className="mt-0.5 flex-shrink-0" />
                          <span>{error}</span>
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Default phase configurations for PropAlgos builds
export const createDefaultPhases = (): PhaseInfo[] => [
  {
    id: 'validation',
    name: 'Pre-Build Validation',
    description: 'Checking quotas, packages, and infrastructure availability',
    status: 'pending',
    icon: <Shield size={20} />,
    estimatedDuration: 30
  },
  {
    id: 'testing',
    name: 'VM Testing',
    description: 'Testing commands in temporary VMs to catch issues early',
    status: 'pending',
    icon: <TestTube size={20} />,
    estimatedDuration: 120
  },
  {
    id: 'building',
    name: 'Image Building',
    description: 'Running Packer to create production-ready images',
    status: 'pending',
    icon: <Hammer size={20} />,
    estimatedDuration: 1800
  },
  {
    id: 'deployment',
    name: 'Image Deployment',
    description: 'Copying images across regions and validating',
    status: 'pending',
    icon: <Upload size={20} />,
    estimatedDuration: 300
  },
  {
    id: 'complete',
    name: 'Complete',
    description: 'Build pipeline finished successfully',
    status: 'pending',
    icon: <Flag size={20} />
  }
];

// Helper to update phase status
export const updatePhaseStatus = (
  phases: PhaseInfo[],
  phaseId: string,
  updates: Partial<PhaseInfo>
): PhaseInfo[] => {
  return phases.map(phase =>
    phase.id === phaseId
      ? { ...phase, ...updates }
      : phase
  );
};

// Helper to get current phase
export const getCurrentPhase = (phases: PhaseInfo[]): PhaseInfo | undefined => {
  return phases.find(p => p.status === 'in_progress');
};

// Helper to calculate total progress percentage
export const calculateProgress = (phases: PhaseInfo[]): number => {
  const completedCount = phases.filter(p => p.status === 'completed').length;
  return Math.round((completedCount / phases.length) * 100);
};
